/**
 * Favorites Utility Functions
 * 
 * Helpers for merging viewing sessions into history,
 * computing insights, and formatting durations.
 */

import { ResourceSession, ResourceHistory, ResourceInsights, UserActivityEvent } from '../types/userActivity';
import { UserFavorite } from '../types/userFavorites';

const MAX_SESSIONS = 50;
const MIN_DURATION_MS = 2000; // Ignore sessions shorter than 2 seconds

/**
 * Merge a new viewing session into existing history.
 * Caps sessions at MAX_SESSIONS to prevent payload bloat.
 */
export function mergeSessionIntoHistory(
  existing: ResourceHistory | undefined,
  event: UserActivityEvent
): ResourceHistory {
  if (event.durationMs < MIN_DURATION_MS) {
    return existing || createEmptyHistory(event.openedAt);
  }

  const newSession: ResourceSession = {
    openedAt: event.openedAt,
    closedAt: event.closedAt,
    durationMs: event.durationMs,
  };

  if (!existing) {
    return {
      sessions: [newSession],
      totalTimeMs: event.durationMs,
      sessionCount: 1,
      lastAccessedAt: event.closedAt,
      firstAccessedAt: event.openedAt,
    };
  }

  const sessions = [...existing.sessions, newSession].slice(-MAX_SESSIONS);

  return {
    sessions,
    totalTimeMs: existing.totalTimeMs + event.durationMs,
    sessionCount: existing.sessionCount + 1,
    lastAccessedAt: event.closedAt,
    firstAccessedAt: existing.firstAccessedAt,
  };
}

function createEmptyHistory(timestamp: string): ResourceHistory {
  return {
    sessions: [],
    totalTimeMs: 0,
    sessionCount: 0,
    lastAccessedAt: timestamp,
    firstAccessedAt: timestamp,
  };
}

/**
 * Format a duration in milliseconds to human-readable string.
 * Examples: "2s", "3m 15s", "1h 20m", "2h"
 */
export function formatDuration(ms: number): string {
  if (ms < 1000) return '0s';
  
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }
  if (minutes > 0) {
    return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  }
  return `${seconds}s`;
}

/**
 * Compute aggregate insights across all favorites.
 */
export function computeInsights(favorites: UserFavorite[]): ResourceInsights {
  let totalFavorites = 0;
  let totalResourcesViewed = 0;
  let totalTimeSpentMs = 0;
  let totalSessions = 0;
  let mostViewedResource: ResourceInsights['mostViewedResource'] = null;
  let longestViewedResource: ResourceInsights['longestViewedResource'] = null;

  for (const fav of favorites) {
    const meta = fav.payload?.metadata;
    // Only count records explicitly marked as favorited
    if (meta?.isFavorited === true) {
      totalFavorites++;
    }

    const history = meta?.history as ResourceHistory | undefined;
    if (history && history.sessionCount > 0) {
      totalResourcesViewed++;
      totalTimeSpentMs += history.totalTimeMs;
      totalSessions += history.sessionCount;

      if (!mostViewedResource || history.sessionCount > mostViewedResource.sessionCount) {
        mostViewedResource = {
          title: fav.payload.title,
          url: fav.payload.url || '',
          sessionCount: history.sessionCount,
        };
      }

      if (!longestViewedResource || history.totalTimeMs > longestViewedResource.totalTimeMs) {
        longestViewedResource = {
          title: fav.payload.title,
          url: fav.payload.url || '',
          totalTimeMs: history.totalTimeMs,
        };
      }
    }
  }

  return {
    totalFavorites,
    totalResourcesViewed,
    totalTimeSpentMs,
    totalSessions,
    mostViewedResource,
    longestViewedResource,
  };
}

/**
 * Get the effective sort date for a favorite (last accessed or updated).
 */
export function getEffectiveSortDate(fav: UserFavorite): number {
  const history = fav.payload?.metadata?.history as ResourceHistory | undefined;
  if (history?.lastAccessedAt) {
    return new Date(history.lastAccessedAt).getTime();
  }
  return new Date(fav.updatedAt).getTime();
}

/**
 * Deduplicate favorites by URL.
 * When multiple records share the same URL, prefer the one with
 * isFavorited: true. Ties are broken by most recently updated.
 */
export function deduplicateByUrl(favorites: UserFavorite[]): UserFavorite[] {
  const urlMap = new Map<string, UserFavorite>();
  const noUrl: UserFavorite[] = [];

  for (const fav of favorites) {
    const url = fav.payload?.url;
    if (!url) {
      noUrl.push(fav);
      continue;
    }

    const existing = urlMap.get(url);
    if (!existing) {
      urlMap.set(url, fav);
      continue;
    }

    const favFlagged = fav.payload?.metadata?.isFavorited === true;
    const existingFlagged = existing.payload?.metadata?.isFavorited === true;

    // Prefer the record with isFavorited: true; fall back to most recently updated
    if (favFlagged && !existingFlagged) {
      urlMap.set(url, fav);
    } else if (!favFlagged && existingFlagged) {
      // keep existing
    } else {
      const keeper = new Date(fav.updatedAt) > new Date(existing.updatedAt) ? fav : existing;
      urlMap.set(url, keeper);
    }
  }

  return [...Array.from(urlMap.values()), ...noUrl];
}
